import React, { useState } from 'react';
import { FlameIcon, MapPinIcon, ShieldAlertIcon, UsersIcon } from 'lucide-react';
import { OnboardingDone, OnboardingFrame, type Step } from '../../components/onboarding/OnboardingFrame';
import { Card } from '../../components/ui/Panels';
import { ButtonLink } from '../../components/ui/Button';
import { Field, Input, Select, Switch, Textarea } from '../../components/ui/Field';
import { allergens, cuisines, diets, equipmentCatalog, occasions } from '../../data/taxonomy';
import { severityChip, severityChipIdle, severityLabel } from '../../data/customer';
import { cn } from '../../utils/cn';
import type { AllergySeverity } from '../../types/customer';
const steps: Step[] = [{
  id: 'where',
  label: 'Where you are',
  title: 'Where should chefs be cooking?',
  blurb: 'We only show you chefs whose service area actually covers your address — no browsing dinners you cannot book.'
}, {
  id: 'table',
  label: 'Your table',
  title: 'Who usually eats with you?',
  blurb: 'This becomes the default head count on every request. You can change it per booking.'
}, {
  id: 'safety',
  label: 'Allergies',
  title: 'Anything a chef must not cook with?',
  blurb: 'Severity matters more than the list. This record follows you into every request, chat and quote, and a chef has to acknowledge it before they can price your dinner.'
}, {
  id: 'kitchen',
  label: 'Your kitchen',
  title: 'What can a chef count on?',
  blurb: 'Wok work is the usual sticking point in Bangkok condos. Two answers here save a lot of back and forth.'
}, {
  id: 'taste',
  label: 'Taste',
  title: 'What are you most likely to book?',
  blurb: 'Used to order your search results. Nothing here is shown to chefs.'
}];
interface Entry {
  allergen: string;
  severity: AllergySeverity;
}
export function CustomerOnboarding() {
  const [index, setIndex] = useState(0);
  const [done, setDone] = useState(false);
  const [address, setAddress] = useState('');
  const [district, setDistrict] = useState('Phrom Phong');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState(1);
  const [occasion, setOccasion] = useState(occasions[0]);
  const [entries, setEntries] = useState<Entry[]>([]);
  const [notes, setNotes] = useState('');
  const [hob, setHob] = useState('gas');
  const [equipment, setEquipment] = useState<string[]>(['wok', 'sharp_knives', 'rice_cooker']);
  const [dishwasher, setDishwasher] = useState(false);
  const [spice, setSpice] = useState(3);
  const [likedCuisines, setLikedCuisines] = useState<string[]>([]);
  const [selectedDiets, setSelectedDiets] = useState<string[]>([]);
  const toggle = (list: string[], set: (v: string[]) => void, id: string) => set(list.includes(id) ? list.filter(x => x !== id) : [...list, id]);
  const addAllergen = (allergen: string) => {
    if (entries.some(e => e.allergen === allergen)) {
      setEntries(entries.filter(e => e.allergen !== allergen));
      return;
    }
    setEntries([...entries, {
      allergen,
      severity: 'strict'
    }]);
  };
  const canAdvance = index === 0 ? address.trim().length > 3 : index === 4 ? likedCuisines.length > 0 : true;
  if (done) {
    return <OnboardingDone title="You’re set. Let’s find you a chef." body={`We’ve saved your table of ${adults + children}, your kitchen, and ${entries.length ? `${entries.length} allergy ${entries.length === 1 ? 'record' : 'records'}` : 'your preferences'}. Every chef you see from here can actually reach your address.`} primary={<ButtonLink to="/discover" variant="action" size="lg" data-id="element-2056" data-mp="sm710hxjld8k">
            Browse chefs near me
          </ButtonLink>} secondary={<ButtonLink to="/app/account/dietary" variant="outline" size="lg" data-id="element-2057" data-mp="23ziwo5j5epjn">
            Review my dietary record
          </ButtonLink>} data-id="element-2055" data-mp="1bd7wgvzgeqnd" />;
  }
  const aside = index === 2 ? <Card className="border-danger/30 bg-danger-soft p-5" data-id="element-2058" data-mp="8jucabbbatg5">
        <div className="flex items-center gap-2" data-id="element-2059" data-mp="8lf4hua5m0au">
          <ShieldAlertIcon className="h-4 w-4 text-danger" aria-hidden="true" data-id="element-2060" data-mp="di5napdcf9zy" />
          <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-danger" data-id="element-2061" data-mp="ii0bgl5qhlhb">Be precise</h2>
        </div>
        <p className="mt-2 text-[13px] leading-relaxed text-danger" data-id="element-2062" data-mp="mjlwitsf962l">
          “Avoid” lets a chef leave something out. “Strict” and “anaphylaxis” change how they shop, which pans they bring,
          and whether they take the booking at all.
        </p>
      </Card> : index === 3 ? <Card className="p-5" data-id="element-2063" data-mp="117q6989hy798">
        <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-2064" data-mp="119kp8h17jpao">Why we ask</h2>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2" data-id="element-2065" data-mp="166tbasph8dh7">
          When a chef quotes, we check their menu against this list and warn them what to bring. It is the difference
          between a wok dish arriving charred and arriving steamed.
        </p>
      </Card> : <Card className="p-5" data-id="element-2066" data-mp="2zr4shke14tlm">
        <h2 className="text-[12px] font-semibold uppercase tracking-[0.05em] text-ink-3" data-id="element-2067" data-mp="2znyzvv66z9l5">Takes two minutes</h2>
        <p className="mt-2 text-[13px] leading-relaxed text-ink-2" data-id="element-2068" data-mp="2utblwq8tdxkt">
          Everything here is editable later under Account. You can skip ahead and fill it in when you request your first
          dinner.
        </p>
      </Card>;
  return <OnboardingFrame steps={steps} index={index} onBack={() => setIndex(i => Math.max(0, i - 1))} onNext={() => index === steps.length - 1 ? setDone(true) : setIndex(i => i + 1)} nextLabel={index === steps.length - 1 ? 'Finish and browse chefs' : 'Continue'} nextDisabled={!canAdvance} exitTo="/discover" aside={aside} data-id="element-2069" data-mp="3ifi1bxkkifl2">
      {index === 0 && <div className="grid max-w-xl gap-4" data-id="element-2070" data-mp="607njckyrd9x">
          <Field label="Street address" id="ob-address" hint="only shared with a chef once you accept their quote" data-id="element-2071" data-mp="60zda6rvqgsa">
            <Input id="ob-address" value={address} onChange={e => setAddress(e.target.value)} placeholder="Siri Residence, Soi Sukhumvit 24, Unit 12B" autoComplete="street-address" data-id="element-2072" data-mp="k4ybpp9pjwvr" />
          </Field>
          <Field label="District" id="ob-district" data-id="element-2073" data-mp="bajtpexsm88y">
            <Select id="ob-district" value={district} onChange={e => setDistrict(e.target.value)} data-id="element-2074" data-mp="fcna8hzbk313">
              {['Phrom Phong', 'Thonglor', 'Ari', 'Sathorn', 'Silom', 'Asok', 'Ratchada', 'On Nut'].map(d => <option key={d} value={d} data-id="element-2075" data-mp="6el2zkozse5d">
                  {d}
                </option>)}
            </Select>
          </Field>
          <div className="flex items-start gap-2.5 rounded-lg bg-oat p-3.5" data-id="element-2076" data-mp="3sk86sdf6k1u1">
            <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" data-id="element-2077" data-mp="3smb6jp64x9by" />
            <p className="text-[13px] leading-relaxed text-ink-2" data-id="element-2078" data-mp="1mpfdffbaqzwd">
              Chefs see your district while quoting and your full address only after you accept. Travel beyond a chef’s
              included radius appears as its own quote line.
            </p>
          </div>
        </div>}

      {index === 1 && <div className="grid max-w-xl gap-4" data-id="element-2079" data-mp="3l0p8z11a241e">
          <div className="grid grid-cols-2 gap-4" data-id="element-2080" data-mp="3l2jrk8p2tk2b">
            <Field label="Adults" id="ob-adults" data-id="element-2081" data-mp="3gaijz02ushim">
              <Input id="ob-adults" type="number" min={1} max={30} value={adults} onChange={e => setAdults(Number(e.target.value) || 1)} className="tnum" data-id="element-2082" data-mp="3g8p9tat17f5v" />
            </Field>
            <Field label="Children" id="ob-children" data-id="element-2083" data-mp="7tns0fnlghjb">
              <Input id="ob-children" type="number" min={0} max={20} value={children} onChange={e => setChildren(Number(e.target.value) || 0)} className="tnum" data-id="element-2084" data-mp="2uuamc56s6dv" />
            </Field>
          </div>
          <Field label="What you are most likely booking for" id="ob-occasion" data-id="element-2085" data-mp="1jq9p4ewur0rh">
            <Select id="ob-occasion" value={occasion} onChange={e => setOccasion(e.target.value)} data-id="element-2086" data-mp="1nruuzgayyes6">
              {occasions.map(o => <option key={o} value={o} data-id="element-2087" data-mp="1sk4qdbuus71u">
                  {o}
                </option>)}
            </Select>
          </Field>
          <div className="flex items-center gap-2.5 rounded-lg bg-primary-soft p-3.5" data-id="element-2088" data-mp="10sox4yysjm2n">
            <UsersIcon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" data-id="element-2089" data-mp="14tiknr8bmyv0" />
            <p className="text-[13px] text-primary" data-id="element-2090" data-mp="14vliuyjmnyat">
              We will default your requests to <span className="tnum font-semibold" data-id="element-2091" data-mp="19uy8fr1vvjh3">{adults + children} guests</span>.
            </p>
          </div>
        </div>}

      {index === 2 && <div className="max-w-2xl" data-id="element-2092" data-mp="2l78r34mwxz7t">
          <p className="mb-2 text-[13px] font-semibold text-ink" data-id="element-2093" data-mp="2la4sj2xyz610">Tap anything that applies</p>
          <div className="flex flex-wrap gap-1.5" data-id="element-2094" data-mp="2q97wek27r67e">
            {allergens.map(allergen => {
          const entry = entries.find(e => e.allergen === allergen);
          return <button key={allergen} type="button" aria-pressed={!!entry} onClick={() => addAllergen(allergen)} className={cn('rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out', entry ? severityChip[entry.severity] : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-2095" data-mp="9mrropqpf8e2">
                  {allergen}
                  {entry && <span className="ml-1.5 opacity-70" data-id="element-2096" data-mp="3d1x4pjfmeak0">
                      {entry.severity === 'avoid' ? 'avoid' : entry.severity === 'strict' ? 'strict' : 'risk'}
                    </span>}
                </button>;
        })}
          </div>

          {!!entries.length && <ul className="mt-5 space-y-2.5" data-id="element-2097" data-mp="5d5jkx6ewoms">
              {entries.map(entry => <li key={entry.allergen} className="rounded-lg border border-hairline p-3.5" data-id="element-2098" data-mp="fvigk9nuoj4">
                  <p className="text-sm font-semibold text-ink" data-id="element-2099" data-mp="fcmmx7y6yqfl">{entry.allergen}</p>
                  <div className="mt-2 flex flex-wrap gap-1.5" data-id="element-2100" data-mp="adji9om4im85">
                    {(['avoid', 'strict', 'anaphylaxis'] as AllergySeverity[]).map(level => <button key={level} type="button" aria-pressed={entry.severity === level} onClick={() => setEntries(entries.map(e => e.allergen === entry.allergen ? {
              ...e,
              severity: level
            } : e))} className={cn('rounded-full border px-3 py-1 text-[12px] font-medium transition-colors duration-150 ease-out', entry.severity === level ? severityChip[level] : severityChipIdle[level])} data-id="element-2101" data-mp="3iqz3pulrylhn">
                        {severityLabel[level]}
                      </button>)}
                  </div>
                </li>)}
            </ul>}

          <Field label="Anything else a chef should know" id="ob-notes" hint="optional" className="mt-5 max-w-xl" data-id="element-2102" data-mp="15llrvqiabexm">
            <Textarea id="ob-notes" rows={3} value={notes} onChange={e => setNotes(e.target.value)} placeholder="My daughter eats separately and needs everything mild." data-id="element-2103" data-mp="1td9a7k1hdhoa" />
          </Field>
        </div>}

      {index === 3 && <div className="max-w-2xl" data-id="element-2104" data-mp="1ob1sedtyz7e1">
          <p className="mb-2 text-[13px] font-semibold text-ink" data-id="element-2105" data-mp="8wco68dvzdsc">Hob</p>
          <div className="flex flex-wrap gap-1.5" data-id="element-2106" data-mp="8y7clwod9o16">
            {[{
          id: 'gas',
          label: 'Gas'
        }, {
          id: 'induction',
          label: 'Induction'
        }, {
          id: 'electric',
          label: 'Electric coil'
        }, {
          id: 'ceramic',
          label: 'Ceramic'
        }].map(option => <button key={option.id} type="button" aria-pressed={hob === option.id} onClick={() => setHob(option.id)} className={cn('rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out', hob === option.id ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-2107" data-mp="2anmm3dih78ra">
                {option.label}
              </button>)}
          </div>

          <p className="mb-2 mt-6 text-[13px] font-semibold text-ink" data-id="element-2108" data-mp="1hyfn6df40h5i">What you own</p>
          <ul className="grid gap-1.5 sm:grid-cols-2" data-id="element-2109" data-mp="3h972hmqdm1rj">
            {equipmentCatalog.slice(0, 10).map(item => {
          const on = equipment.includes(item.id);
          return <li key={item.id} data-id="element-2110" data-mp="3kr4ufxq5wljh">
                  <button type="button" aria-pressed={on} onClick={() => toggle(equipment, setEquipment, item.id)} className={cn('flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-[13px] transition-colors duration-150 ease-out', on ? 'border-primary/40 bg-primary-soft font-medium text-ink' : 'border-hairline text-ink-3 hover:text-ink')} data-id="element-2111" data-mp="3kptnjp1f6xo5">
                    <span className={cn('h-4 w-4 shrink-0 rounded', on ? 'bg-primary' : 'border border-hairline')} aria-hidden="true" data-id="element-2112" data-mp="3at75f80hakpt" />
                    {item.label}
                  </button>
                </li>;
        })}
          </ul>

          <div className="mt-4 max-w-xl border-t border-hairline" data-id="element-2113" data-mp="v119i87f3zve">
            <Switch checked={dishwasher} onChange={setDishwasher} label="Dishwasher" description="Shortens clear-down." data-id="element-2114" data-mp="23yr8twwp2ysc" />
          </div>
        </div>}

      {index === 4 && <div className="max-w-2xl" data-id="element-2115" data-mp="2czpfkmyipkem">
          <p className="mb-2 text-[13px] font-semibold text-ink" data-id="element-2116" data-mp="2d0qqmrfja6wc">Cuisines you would book first</p>
          <div className="flex flex-wrap gap-1.5" data-id="element-2117" data-mp="27ztdit9ds31p">
            {cuisines.map(cuisine => {
          const on = likedCuisines.includes(cuisine.id);
          return <button key={cuisine.id} type="button" aria-pressed={on} onClick={() => toggle(likedCuisines, setLikedCuisines, cuisine.id)} className={cn('rounded-full border px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out', on ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-2118" data-mp="2hztwuf792j5w">
                  {cuisine.label}
                </button>;
        })}
          </div>

          <p className="mb-2 mt-6 text-[13px] font-semibold text-ink" data-id="element-2119" data-mp="3p9wjjnfeedj7">Diets at your table</p>
          <div className="flex flex-wrap gap-1.5" data-id="element-2120" data-mp="3accksy93helh">
            {diets.map(diet => {
          const on = selectedDiets.includes(diet);
          return <button key={diet} type="button" aria-pressed={on} onClick={() => toggle(selectedDiets, setSelectedDiets, diet)} className={cn('rounded-full border px-3 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-out', on ? 'border-primary bg-primary-soft text-primary' : 'border-hairline text-ink-2 hover:text-ink')} data-id="element-2121" data-mp="31kajyyv45wao">
                  {diet}
                </button>;
        })}
          </div>

          <div className="mt-7 max-w-md" data-id="element-2122" data-mp="t545bj07biki">
            <label htmlFor="ob-spice" className="flex items-center justify-between gap-3 text-[13px] font-semibold text-ink" data-id="element-2123" data-mp="t65ipu4owh5e">
              <span className="flex items-center gap-1.5" data-id="element-2124" data-mp="jf13ctb3qua1">
                <FlameIcon className="h-4 w-4 text-action" aria-hidden="true" data-id="element-2125" data-mp="jfkki7sxhen0" />
                Chilli tolerance
              </span>
              <span className="tnum text-ink-2" data-id="element-2126" data-mp="ee3mck1tw6a4">{spice} / 5</span>
            </label>
            <input id="ob-spice" type="range" min={1} max={5} value={spice} onChange={e => setSpice(Number(e.target.value))} className="mt-2 w-full accent-action" data-id="element-2127" data-mp="1c1m4glhdqkqs" />
            <p className="mt-1 text-[12px] text-ink-3" data-id="element-2128" data-mp="y3e1nwxnwp7p">Thai chefs ask this first. 1 is no heat, 5 is as the dish is meant to be.</p>
          </div>
        </div>}
    </OnboardingFrame>;
}